import { Colors, DateInput, Icon } from '@amelio/ui-library';
import { useEffect, useRef, useState } from 'react';

import type { EvaluationPeriodOption } from '../types';
import type { DateInputProps } from '@amelio/ui-library';

import { Locale } from '@/constants/Locale';
import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';
import { formatPeriodRange, formatShortDate } from '@/utils/periodRange';

// Sentinel value used as the selected period when the manager picks a custom From/To range.
export const CUSTOM_PERIOD_VALUE = 'custom';

export type DateFieldAppearance = Pick<
    DateInputProps,
    | 'variant'
    | 'iconPlacement'
    | 'valueTextClassName'
    | 'idleBorderClassName'
    | 'placeholderTextClassName'
    | 'clearAppearance'
    | 'hideFocusBorderWhenOpen'
    | 'heightClassName'
>;

export interface CustomRangeOptions {
    appearance?: DateFieldAppearance;
    fromPlaceholder?: string;
    toPlaceholder?: string;
    prefillsOnSelect?: boolean;
    enforcesChronology?: boolean;
}

interface EvaluationDatePeriodProps {
    evaluationDate?: Date;
    onDateChange?: (date: Date) => void;
    selectedPeriod: string;
    periods: EvaluationPeriodOption[];
    onPeriodChange: (value: string) => void;
    fromDate?: Date;
    toDate?: Date;
    onFromDateChange: (date?: Date) => void;
    onToDateChange: (date?: Date) => void;
    isPeriodInherited?: boolean;
    children?: React.ReactNode;
    disabled?: boolean;
    readOnly?: boolean;
    dataTestId?: string;
    surface?: 'default' | 'flat';
    customRange?: CustomRangeOptions;
    errorMessage?: string;
}

export const SURFACE_CLASSES: Record<NonNullable<EvaluationDatePeriodProps['surface']>, string> = {
    default: 'rounded-2xl border-white/60 bg-white/60 shadow-sm backdrop-blur-md',
    flat: 'rounded-[12px] border-[var(--color-cycle-tile-idle-border)] bg-[var(--color-cycle-tile-idle-surface)]',
};

const RANGE_ROW_CLASS =
    'flex flex-nowrap max-lg:flex-col max-lg:items-start max-lg:gap-2 lg:flex-row lg:items-center lg:gap-3';

const RANGE_LABEL_CLASS =
    'shrink-0 text-[12px] font-semibold tracking-wider text-gray-400 uppercase';

const RANGE_FIELD_CLASS =
    'relative z-50 min-w-0 max-lg:w-full lg:max-w-fit lg:flex-1 lg:overflow-hidden';

const RANGE_END_GROUP_CLASS = 'max-lg:contents lg:flex lg:items-center lg:gap-3';

const RANGE_ARROW_CLASS = 'max-lg:hidden';

export const EvaluationDatePeriod = ({
    evaluationDate,
    onDateChange,
    selectedPeriod,
    periods,
    onPeriodChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    isPeriodInherited = false,
    children,
    disabled = false,
    readOnly = false,
    dataTestId = 'evaluation-date-period',
    surface = 'default',
    customRange: {
        appearance: customRangeAppearance,
        fromPlaceholder,
        toPlaceholder,
        prefillsOnSelect = true,
        enforcesChronology = false,
    } = {},
    errorMessage,
}: EvaluationDatePeriodProps) => {
    const { t } = useAmelioTranslations();

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const isReadOnly = readOnly && !disabled;

    const isInert = disabled || isReadOnly;

    const isCustom = selectedPeriod === CUSTOM_PERIOD_VALUE;
    const lang = i18n.language === Locale.EN ? 'en' : 'fr';

    const endDateFloor = enforcesChronology && fromDate ? { before: fromDate } : undefined;

    const customLabel =
        fromDate && toDate
            ? `${formatShortDate({ date: fromDate, language: lang })} - ${formatShortDate({ date: toDate, language: lang })}`
            : t('managerAssessmentEvaluation.customPeriodOption');

    const selectedPredefined = isCustom
        ? undefined
        : periods.find((period) => period.value === selectedPeriod);

    const selectedPeriodLabel = isCustom
        ? customLabel
        : (selectedPredefined?.label ?? t('managerAssessmentEvaluation.selectPeriod'));

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Use composedPath() rather than event.target: when the app is embedded in a Shadow DOM
            // (Module Federation), event.target is retargeted to the shadow host, so contains()
            // always returns false and would close the dropdown before the option's click fires —
            // making the options (e.g. Custom) unclickable. composedPath() crosses the boundary.
            if (dropdownRef.current && !event.composedPath().includes(dropdownRef.current)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isDropdownOpen]);

    const handlePeriodSelect = (periodValue: string) => {
        if (isInert) {
            return;
        }
        onPeriodChange(periodValue);
        setIsDropdownOpen(false);

        if (prefillsOnSelect && periodValue === CUSTOM_PERIOD_VALUE && !fromDate && !toDate) {
            const today = new Date();
            const inThreeMonths = new Date(today);
            inThreeMonths.setMonth(inThreeMonths.getMonth() + 3);
            onFromDateChange(today);
            onToDateChange(inThreeMonths);
        }
    };

    return (
        <div
            className={`relative z-40 flex w-full max-w-[842px] flex-col gap-4 rounded-2xl border border-white/60 bg-white/60 p-4 shadow-sm backdrop-blur-md md:!gap-[20px] md:!p-[24px] ${SURFACE_CLASSES[surface]}`}
            data-testid={dataTestId}
        >
            {evaluationDate && (
                <>
                    <div
                        className={
                            'relative z-[60] flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6'
                        }
                    >
                        <label className={'text-[14px] font-semibold text-gray-900 sm:w-[200px]'}>
                            {t('managerAssessmentEvaluation.evaluationDate')}
                        </label>

                        <div className={'relative z-50 w-56 md:!w-[220px]'}>
                            <DateInput
                                value={evaluationDate}
                                onChange={(date) => date && !isInert && onDateChange?.(date)}
                                lang={lang}
                                placeholder={t('managerAssessmentEvaluation.evaluationDate')}
                                clearable={false}
                                disabled={disabled}
                                readOnly={isReadOnly}
                                dataTestId={'evaluation-date'}
                                inputClassName={'!rounded-[12px] py-[8px]! px-[16px]!'}
                            />
                        </div>
                    </div>

                    <div
                        className={
                            'h-px w-full bg-linear-to-r from-transparent via-purple-100 to-transparent'
                        }
                    />
                </>
            )}

            <div
                className={'relative z-50 flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-6'}
            >
                <label className={'text-[14px] font-semibold text-gray-900 sm:w-[200px] sm:pt-2'}>
                    {t('managerAssessmentEvaluation.evaluationPeriod')}
                </label>

                <div className={'flex flex-1 flex-col gap-[16px]'}>
                    <div
                        className={'relative'}
                        ref={dropdownRef}
                    >
                        <button
                            type={'button'}
                            disabled={disabled}
                            aria-readonly={isReadOnly || undefined}
                            aria-expanded={isReadOnly ? undefined : isDropdownOpen}
                            onClick={() => !isInert && setIsDropdownOpen(!isDropdownOpen)}
                            className={`group flex w-full items-center justify-between gap-3 rounded-xl border border-purple-100 bg-white/80 px-4 py-2 text-left shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                                isReadOnly
                                    ? 'cursor-default'
                                    : 'cursor-pointer hover:border-teal-400/30'
                            }`}
                            data-testid={'evaluation-period-button'}
                            aria-describedby={
                                isPeriodInherited ? 'evaluation-period-inherited-hint' : undefined
                            }
                        >
                            <span
                                className={
                                    'truncate text-sm font-medium text-gray-900 transition-colors group-hover:text-teal-500'
                                }
                            >
                                {selectedPeriodLabel}
                            </span>

                            <Icon
                                name={'arrow-arrow-down'}
                                size={20}
                                color={Colors.BASE_300}
                            />
                        </button>

                        {isDropdownOpen && (
                            <div
                                className={
                                    'animate-in fade-in slide-in-from-top-2 absolute top-full right-0 left-0 z-9999 mt-2 w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl duration-200'
                                }
                                data-testid={'evaluation-period-dropdown'}
                            >
                                <div
                                    className={'scrollbar-glass max-h-[400px] overflow-y-auto py-1'}
                                >
                                    <button
                                        type={'button'}
                                        disabled={disabled}
                                        onClick={() => handlePeriodSelect(CUSTOM_PERIOD_VALUE)}
                                        className={`flex w-full items-center justify-between px-4 py-4 text-left transition-colors hover:bg-purple-50 disabled:opacity-50 ${isCustom ? 'bg-purple-50/70 text-teal-500' : 'text-gray-700'}`}
                                        data-testid={'period-option-custom'}
                                    >
                                        <span className={'text-base-900 text-sm font-medium'}>
                                            {t('managerAssessmentEvaluation.customPeriodOption')}
                                        </span>

                                        {isCustom && (
                                            <Icon
                                                name={'essential-checkmark'}
                                                size={16}
                                                color={Colors.PRIMARY}
                                            />
                                        )}
                                    </button>

                                    <div
                                        className={
                                            'mt-1 border-t border-gray-100 px-4 py-4 text-xs font-semibold tracking-wider text-gray-400 uppercase'
                                        }
                                    >
                                        {t('managerAssessmentEvaluation.predefinedPeriodsHeader')}
                                    </div>

                                    {periods.map((period) => {
                                        const isSelected = selectedPeriod === period.value;

                                        return (
                                            <button
                                                key={period.value}
                                                type={'button'}
                                                disabled={disabled}
                                                onClick={() => handlePeriodSelect(period.value)}
                                                className={`flex w-full items-center justify-between px-4 py-4 text-left transition-colors hover:bg-purple-50 disabled:opacity-50 ${isSelected ? 'bg-purple-50/70 text-teal-500' : 'text-gray-700'}`}
                                                data-testid={`period-option-${period.value}`}
                                            >
                                                <div className={'flex flex-col gap-0.5'}>
                                                    <span
                                                        className={`text-base-900 text-sm font-medium ${isSelected ? '!text-teal-400' : ''}`}
                                                    >
                                                        {period.label}
                                                    </span>

                                                    <span className={'text-sm text-gray-400'}>
                                                        {formatPeriodRange({
                                                            from: period.from,
                                                            to: period.to,
                                                            language: lang,
                                                        })}
                                                    </span>
                                                </div>

                                                {isSelected && (
                                                    <Icon
                                                        name={'essential-checkmark'}
                                                        size={16}
                                                        color={Colors.PRIMARY}
                                                    />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {isPeriodInherited && (
                        <p
                            id={'evaluation-period-inherited-hint'}
                            className={'mt-1 text-xs text-gray-500'}
                            data-testid={'evaluation-period-inherited-hint'}
                        >
                            {t('managerAssessmentEvaluation.evaluationPeriodInheritedHint')}
                        </p>
                    )}

                    {isCustom && (
                        <div
                            className={RANGE_ROW_CLASS}
                            data-testid={'custom-period-range'}
                        >
                            <span className={RANGE_LABEL_CLASS}>
                                {t('managerAssessmentEvaluation.periodFrom')}
                            </span>

                            <div
                                className={RANGE_FIELD_CLASS}
                                data-testid={'period-from-field'}
                            >
                                <DateInput
                                    {...customRangeAppearance}
                                    portalPanel
                                    value={fromDate}
                                    onChange={(date) => !isInert && onFromDateChange(date)}
                                    lang={lang}
                                    placeholder={
                                        fromPlaceholder ??
                                        t('managerAssessmentEvaluation.periodFrom')
                                    }
                                    clearable={false}
                                    disabled={disabled}
                                    readOnly={isReadOnly}
                                    dataTestId={'period-from'}
                                    iconPlacement={'leading'}
                                    inputClassName={
                                        '!rounded-[12px] py-[8px]! px-[16px]! h-auto! text-[14px]!'
                                    }
                                />
                            </div>

                            <div
                                className={RANGE_END_GROUP_CLASS}
                                data-testid={'custom-period-range-end'}
                            >
                                <span
                                    className={RANGE_ARROW_CLASS}
                                    data-testid={'custom-period-range-arrow'}
                                >
                                    <Icon
                                        name={'arrow-arrow-right'}
                                        size={16}
                                        color={Colors.BASE_300}
                                    />
                                </span>

                                <span className={RANGE_LABEL_CLASS}>
                                    {t('managerAssessmentEvaluation.periodTo')}
                                </span>

                                <div
                                    className={RANGE_FIELD_CLASS}
                                    data-testid={'period-to-field'}
                                >
                                    <DateInput
                                        {...customRangeAppearance}
                                        portalPanel
                                        value={toDate}
                                        onChange={(date) => !isInert && onToDateChange(date)}
                                        lang={lang}
                                        placeholder={
                                            toPlaceholder ??
                                            t('managerAssessmentEvaluation.periodTo')
                                        }
                                        clearable={false}
                                        disabledDates={endDateFloor}
                                        disabled={disabled}
                                        readOnly={isReadOnly}
                                        dataTestId={'period-to'}
                                        iconPlacement={'leading'}
                                        inputClassName={
                                            '!rounded-[12px] py-[8px]! px-[16px]! h-auto! text-[14px]!'
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {isCustom && errorMessage && (
                        <div
                            className={'flex items-center gap-1'}
                            role={'alert'}
                            data-testid={'custom-period-error'}
                        >
                            <Icon
                                name={'essential-danger'}
                                size={16}
                                color={Colors.ERROR}
                            />
                            <span
                                className={'text-sm'}
                                style={{ color: Colors.ERROR }}
                            >
                                {errorMessage}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {children}
        </div>
    );
};
